import { FormikErrors, useFormikContext } from 'formik'
import { Fragment, ReactElement } from 'react'
import { FormattedMessage, defineMessages, useIntl } from 'react-intl'
import styled from 'styled-components'

import FormLayout from 'components/v2/FormLayout'
import Heading from 'components/v2/Heading'
import { Row } from 'components/v2/Row'
import { Column } from 'components/v2/Column'
import { Input } from 'components/Input'
import { Button } from 'components/v2/Button'
import { CancelIcon } from 'components/Icons/CancelIcon'
import { IBusinessSiteContact, INewBusinessSiteValues } from 'features/BusinessSites/types'

const messages = defineMessages({
  namePlaceholder: {
    id: 'businessSites.form.contacts.name.placeholder',
    defaultMessage: 'Enter a name'
  },
  phonePlaceholder: {
    id: 'businessSites.form.contacts.phone.placeholder',
    defaultMessage: 'Enter phone'
  },
  emailPlaceholder: {
    id: 'businessSites.form.contacts.email.placeholder',
    defaultMessage: 'Enter an email'
  }
})

const TitleWrapper = styled.div`
  align-items: center;
  display: flex;
  justify-content: space-between;
`

const CancelIconWrapper = styled.div`
  cursor: pointer;
  display: flex;
`

export const ContactsFormCard = (): ReactElement => {
  const intl = useIntl()
  const { values, handleChange, handleBlur, touched, errors, setFieldValue } =
    useFormikContext<INewBusinessSiteValues>()

  const addContact = (): void => {
    const newContact: IBusinessSiteContact = { name: '', phone: '', secondaryPhone: '', email: '', secondaryEmail: '' }
    setFieldValue('contacts', [...values.contacts, newContact])
  }

  return (
    <FormLayout.Card small>
      {values.contacts.map((contact, index): ReactElement => {
        const isFirstContact = index === 0

        const removeContact = (): void => {
          const contacts = [...values.contacts]
          contacts.splice(index, 1)
          setFieldValue('contacts', contacts)
        }

        return (
          <Fragment key={index}>
            {isFirstContact ? (
              <TitleWrapper>
                <Heading.H4>
                  <FormattedMessage id="businessSites.form.section.firstContact.title" defaultMessage="Contact" />
                </Heading.H4>
                <Button type="button" kind="secondary" onClick={addContact}>
                  <FormattedMessage id="businessSites.form.section.contacts.add" defaultMessage="Add contact" />
                </Button>
              </TitleWrapper>
            ) : (
              <TitleWrapper>
                <Heading.H4>
                  <FormattedMessage
                    id="businessSites.form.section.nextContact.title"
                    defaultMessage="Contact {contactPosition}"
                    values={{
                      contactPosition: index + 1
                    }}
                  />
                </Heading.H4>
                <CancelIconWrapper onClick={removeContact}>
                  <CancelIcon gray />
                </CancelIconWrapper>
              </TitleWrapper>
            )}
            <Row key={index} noMargin>
              <Column width="50%">
                <Input
                  id={`contacts[${index}].name`}
                  value={contact.name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder={intl.formatMessage(messages.namePlaceholder)}
                  label={<FormattedMessage id="businessSites.form.contacts.name.label" defaultMessage="Name" />}
                  touched={touched.contacts?.[index]?.name}
                  errors={(errors.contacts?.[index] as FormikErrors<IBusinessSiteContact>)?.name}
                />
              </Column>
              <Column>
                <Input
                  id={`contacts[${index}].phone`}
                  type="tel"
                  value={contact.phone}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder={intl.formatMessage(messages.phonePlaceholder)}
                  label={<FormattedMessage id="businessSites.form.contacts.phone.label" defaultMessage="Phone" />}
                  touched={touched.contacts?.[index]?.phone}
                  errors={(errors.contacts?.[index] as FormikErrors<IBusinessSiteContact>)?.phone}
                />
              </Column>
              <Column>
                <Input
                  id={`contacts[${index}].secondaryPhone`}
                  type="tel"
                  value={contact.secondaryPhone}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder={intl.formatMessage(messages.phonePlaceholder)}
                  label={
                    <FormattedMessage
                      id="businessSites.form.contacts.secondaryPhone.label"
                      defaultMessage="Secondary phone"
                    />
                  }
                  touched={touched.contacts?.[index]?.secondaryPhone}
                  errors={(errors.contacts?.[index] as FormikErrors<IBusinessSiteContact>)?.secondaryPhone}
                  optional
                />
              </Column>
            </Row>
            <Row noMargin>
              <Column>
                <Input
                  id={`contacts[${index}].email`}
                  value={contact.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder={intl.formatMessage(messages.emailPlaceholder)}
                  label={<FormattedMessage id="businessSites.form.contacts.email.label" defaultMessage="Email" />}
                  touched={touched.contacts?.[index]?.email}
                  errors={(errors.contacts?.[index] as FormikErrors<IBusinessSiteContact>)?.email}
                />
              </Column>
              <Column>
                <Input
                  id={`contacts[${index}].secondaryEmail`}
                  value={contact.secondaryEmail}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder={intl.formatMessage(messages.emailPlaceholder)}
                  label={
                    <FormattedMessage
                      id="businessSites.form.contacts.secondaryEmail.label"
                      defaultMessage="Secondary email"
                    />
                  }
                  touched={touched.contacts?.[index]?.secondaryEmail}
                  errors={(errors.contacts?.[index] as FormikErrors<IBusinessSiteContact>)?.secondaryEmail}
                  optional
                />
              </Column>
            </Row>
            {index < values.contacts.length - 1 && <FormLayout.Divider />}
          </Fragment>
        )
      })}
    </FormLayout.Card>
  )
}
